import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Loader2, FileText, CheckCircle2, XCircle, Clock, Users, ShieldCheck, Ban, School,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDay } from '@/pages/coordinator/AssignMentorDialog';

const API_ROOT = process.env.REACT_APP_BACKEND_URL;
const PILL = {
  pending: 'bg-amber-50 text-amber-700',
  accepted: 'bg-emerald-50 text-emerald-600',
  rejected: 'bg-gray-100 text-gray-500',
  withdrawn: 'bg-gray-100 text-gray-400',
};
const LABEL = {
  pending: 'Awaiting your decision', accepted: 'Accepted', rejected: 'Declined',
  withdrawn: 'Withdrawn by the school',
};

/** Shortlisted students for one programme, shown only where the school lets the company decide. */
export default function PartnerShortlistPanel({ programId, onLoaded }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [oppFilter, setOppFilter] = useState('all');
  const [status, setStatus] = useState('all');
  const [deciding, setDeciding] = useState(null);
  const [bulk, setBulk] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/partner/shortlisted');
      setData(r.data);
      onLoaded?.(r.data);
    } catch (e) { setData(e.response?.status === 404 ? { noCompany: true } : null); }
    setLoading(false);
  }, [onLoaded]);
  useEffect(() => { load(); }, [load]);

  const inProgram = (data?.groups || []).filter(g => !programId || g.program_id === programId);
  const groups = inProgram
    .map(g => ({
      ...g,
      opportunities: g.opportunities
        .filter(o => oppFilter === 'all' || o.id === oppFilter)
        .map(o => ({ ...o, students: o.students.filter(s => status === 'all' || s.status === status) }))
        .filter(o => o.students.length > 0),
    }))
    .filter(g => g.opportunities.length > 0);

  const allOpps = inProgram.flatMap(g => g.opportunities.map(o => ({ id: o.id, name: o.title })));
  const counts = inProgram.flatMap(g => g.opportunities).reduce((acc, o) => ({
    pending: acc.pending + (o.pending || 0),
    accepted: acc.accepted + (o.accepted || 0),
    rejected: acc.rejected + (o.rejected || 0),
  }), { pending: 0, accepted: 0, rejected: 0 });

  return (
    <div data-testid="partner-shortlist">
      {loading ? (
        <div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#9CA3AF]" /></div>
      ) : data?.noCompany || !data ? (
        <p className="py-16 text-center text-sm text-[#9CA3AF]" data-testid="shortlist-no-company">
          No company is linked to this account yet.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4">
            {[['pending', 'Awaiting your decision', Clock], ['accepted', 'Accepted', CheckCircle2],
              ['rejected', 'Declined', XCircle]].map(([k, label, Icon]) => (
              <div key={k} className="bg-white rounded-2xl border border-[#E5E7EB] px-4 py-3.5" data-testid={`shortlist-count-${k}`}>
                <p className="text-2xl font-bold text-[#1F2937] font-['Outfit'] leading-none">{counts[k] || 0}</p>
                <p className="text-[11px] text-[#6B7280] mt-1.5 flex items-center gap-1"><Icon className="h-3 w-3" />{label}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Select value={oppFilter} onValueChange={setOppFilter}>
              <SelectTrigger className="h-9 w-[210px] rounded-lg border-gray-200 text-xs bg-white" data-testid="shortlist-opportunity-filter">
                <SelectValue placeholder="All internships" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All internships</SelectItem>
                {allOpps.map(o => <SelectItem key={o.id} value={o.id} className="text-xs">{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-1.5 flex-wrap">
              {[['all', 'All'], ['pending', 'Pending'], ['accepted', 'Accepted'], ['rejected', 'Declined'],
                ['withdrawn', 'Withdrawn']].map(([f, label]) => (
                <button key={f} onClick={() => setStatus(f)} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${status === f ? 'bg-[#1F2937] text-white border-[#1F2937]' : 'bg-white text-[#6B7280] border-gray-200 hover:border-[#9CA3AF]'}`} data-testid={`shortlist-filter-${f}`}>{label}</button>
              ))}
            </div>
          </div>

          {groups.length === 0 ? (
            <p className="py-16 text-center text-sm text-[#9CA3AF] max-w-md mx-auto leading-relaxed" data-testid="shortlist-empty">
              No students to review right now. Schools send you students once they have shortlisted them for your
              internships.
            </p>
          ) : (
            <div className="space-y-4">
              {groups.map(g => (
                <div key={`${g.school_id}-${g.program_id}`} data-testid={`shortlist-group-${g.program_id}`}>
                  {!programId && (
                    <div className="flex items-center gap-2 mb-2">
                      <School className="h-3.5 w-3.5 text-[#F97D6A]" />
                      <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#9CA3AF]">
                        {g.school_name} · {g.program_name}
                      </p>
                    </div>
                  )}
                  <div className="space-y-3">
                    {g.opportunities.map(o => (
                      <div key={o.id} className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden" data-testid={`shortlist-opportunity-${o.id}`}>
                        <div className="px-4 py-3 flex items-start justify-between gap-3 flex-wrap border-b border-[#F3F4F6]">
                          <div>
                            <p className="text-sm font-semibold text-[#1F2937]">{o.title}</p>
                            <p className="text-[11px] text-[#6B7280] mt-0.5 flex items-center gap-x-3 flex-wrap">
                              <span>{formatDay(o.start_date)} – {formatDay(o.end_date)}</span>
                              <span className="flex items-center gap-1"><Users className="h-3 w-3 text-[#9CA3AF]" />{o.accepted} of {o.positions} seats accepted</span>
                              <span>{o.pending} awaiting you</span>
                            </p>
                          </div>
                          {o.pending > 0 && (
                            <Button size="sm" onClick={() => setBulk(o)} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-lg text-xs" data-testid={`accept-all-${o.id}`}>
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Accept all {o.pending} pending
                            </Button>
                          )}
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-left min-w-[680px]">
                            <thead>
                              <tr className="text-[10px] uppercase tracking-wide text-[#9CA3AF] border-b border-[#F7F8F9]">
                                <th className="px-4 py-2 font-semibold">Student</th>
                                <th className="px-3 py-2 font-semibold">Resume</th>
                                <th className="px-3 py-2 font-semibold">Decision</th>
                                <th className="px-3 py-2 font-semibold text-right">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {o.students.map(s => (
                                <tr key={s.id} className="border-b border-[#F7F8F9] last:border-0 hover:bg-[#FAFBFC]" data-testid={`shortlist-row-${s.id}`}>
                                  <td className="px-4 py-3 align-top">
                                    <p className="text-sm font-semibold text-[#1F2937]">{s.student_name}</p>
                                    <p className="text-[11px] text-[#9CA3AF]">
                                      Grade {s.grade}{s.division ? `-${s.division}` : ''} · sent {formatDay(s.sent_at?.slice(0, 10))}
                                    </p>
                                  </td>
                                  <td className="px-3 py-3 align-top">
                                    {s.resume && s.status !== 'withdrawn' ? (
                                      <a href={`${API_ROOT}/api/parent/internship-applications/${s.application_id}/resume`} target="_blank" rel="noreferrer" className="text-[11px] text-[#F97D6A] hover:underline inline-flex items-center gap-1" data-testid={`shortlist-resume-${s.id}`}>
                                        <FileText className="h-3 w-3" />{s.resume.filename || 'Resume'}
                                      </a>
                                    ) : <span className="text-[11px] text-[#9CA3AF]">—</span>}
                                  </td>
                                  <td className="px-3 py-3 align-top">
                                    <Badge className={`border-0 text-[10px] ${PILL[s.status]}`} data-testid={`shortlist-status-${s.id}`}>{LABEL[s.status]}</Badge>
                                    {s.decided_at && (
                                      <p className="text-[10px] text-[#9CA3AF] mt-1">
                                        {s.decided_by} · {formatDay(s.decided_at.slice(0, 10))}
                                      </p>
                                    )}
                                    {s.decision_reason && <p className="text-[10px] text-[#6B7280] mt-0.5">“{s.decision_reason}”</p>}
                                    {s.withdrawn_reason && <p className="text-[10px] text-[#6B7280] mt-0.5">“{s.withdrawn_reason}”</p>}
                                  </td>
                                  <td className="px-3 py-3 align-top text-right">
                                    {s.status === 'pending' ? (
                                      <div className="flex gap-1.5 justify-end">
                                        <Button size="sm" onClick={() => setDeciding({ student: s, decision: 'accept' })} className="bg-emerald-500 text-white hover:bg-emerald-600 rounded-lg text-[11px] h-7" data-testid={`accept-${s.id}`}>Accept</Button>
                                        <Button size="sm" variant="outline" onClick={() => setDeciding({ student: s, decision: 'reject' })} className="rounded-lg text-[11px] h-7 border-gray-200" data-testid={`reject-${s.id}`}>Decline</Button>
                                      </div>
                                    ) : s.status === 'withdrawn' ? (
                                      <span className="text-[11px] text-[#9CA3AF] inline-flex items-center gap-1"><Ban className="h-3 w-3" />No action</span>
                                    ) : <span className="text-[11px] text-[#9CA3AF]">Responded</span>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <p className="text-[11px] text-[#9CA3AF] mt-5 flex items-start gap-1.5 leading-relaxed max-w-2xl" data-testid="shortlist-privacy-note">
            <ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-px" />
            You only see students formally proposed to your company, and resumes open through a secure link. You
            cannot change a student's application or confirm a placement — the school does that.
          </p>
        </>
      )}

      {deciding && (
        <DecisionDialog {...deciding} onClose={() => setDeciding(null)}
          onDone={() => { setDeciding(null); load(); }} />
      )}
      {bulk && (
        <BulkDialog opportunity={bulk} onClose={() => setBulk(null)}
          onDone={() => { setBulk(null); load(); }} />
      )}
    </div>
  );
}

function DecisionDialog({ student, decision, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const accepting = decision === 'accept';

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/partner/shortlisted/${student.id}/decision`, { decision, reason: reason.trim() || null });
      toast.success(accepting ? `${student.student_name} accepted` : `${student.student_name} declined`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not record this decision'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="decision-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">
            {accepting ? `Accept ${student.student_name}?` : `Decline ${student.student_name}?`}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Grade {student.grade}{student.division ? `-${student.division}` : ''} · {student.opportunity_title}
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm text-[#6B7280] leading-relaxed">
          {accepting
            ? 'You are confirming your organisation can host this student. The school makes the final placement and tells the family.'
            : 'The school sees your response and can propose another student for the seat.'}
        </p>
        <div>
          <Label className="text-sm font-medium text-[#1F2937]">
            {accepting ? 'Note for the school (optional)' : 'Reason (optional)'}
          </Label>
          <Textarea value={reason} onChange={e => setReason(e.target.value)} className="mt-1.5 rounded-xl border-gray-200 min-h-[64px] resize-none" data-testid="decision-reason" />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200">Cancel</Button>
          <Button onClick={submit} disabled={saving} className={`rounded-xl font-semibold text-white ${accepting ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-[#F97D6A] hover:bg-[#F06651]'}`} data-testid="confirm-decision-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{accepting ? 'Accept student' : 'Decline student'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkDialog({ opportunity, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const pending = opportunity.students.filter(s => s.status === 'pending');

  const submit = async () => {
    setSaving(true);
    try {
      const r = await api.post('/partner/shortlisted/accept-all', { opportunity_id: opportunity.id });
      toast.success(`${r.data.accepted} student${r.data.accepted === 1 ? '' : 's'} accepted`);
      (r.data.blocked || []).forEach(b => toast.error(`${b.student_name}: ${b.reason}`));
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not accept these students'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="bulk-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">Accept {pending.length} student{pending.length === 1 ? '' : 's'}?</DialogTitle>
          <DialogDescription className="text-xs">
            {opportunity.title} · {opportunity.accepted} of {opportunity.positions} seats already accepted
          </DialogDescription>
        </DialogHeader>
        <ul className="divide-y divide-[#F3F4F6] border border-[#F3F4F6] rounded-xl" data-testid="bulk-review-list">
          {pending.map(s => (
            <li key={s.id} className="px-3 py-2 flex items-center justify-between gap-2">
              <span className="text-sm text-[#1F2937]">{s.student_name} <span className="text-[#9CA3AF]">· grade {s.grade}{s.division ? `-${s.division}` : ''}</span></span>
              <span className="text-[10px] text-[#9CA3AF]">sent {formatDay(s.sent_at?.slice(0, 10))}</span>
            </li>
          ))}
        </ul>
        {pending.length + opportunity.accepted > opportunity.positions && (
          <p className="text-[11px] text-amber-700 leading-relaxed" data-testid="bulk-capacity-warning">
            This is more students than you have seats for — the extras will stay pending until you decline someone or
            the school adjusts the capacity.
          </p>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-500 text-white hover:bg-emerald-600 rounded-xl font-semibold" data-testid="confirm-bulk-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Accept all
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
